import {
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  useTheme
} from "@material-ui/core";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import { LuArrowDown, LuArrowDownUp, LuArrowUp } from "react-icons/lu";
import { useLocation } from "react-router-dom";
import SearchBar from "../../../common/Search";
import { GuideContinueCard, GuideMenu } from "../../../common/GuideUI";
import { takeGuideRequest, isGuideDone, markGuideDone, readGuideProgress, saveGuideProgress } from "../../../common/guides";
import { pageInfoData } from "../../../common/pageInfoData";
import { SpotlightOverlay } from "../../../common/SpotlightOverlay";
import CustomTabs from "../../../common/Tabs";
import CustomTooltip from "../../../common/Tooltip";
import CustomPagination from "../../../components/Common/CustomPagination";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import { apiPut, getCurrentUserId, getCurrentUserOrganizationId, toastError, toastSuccess } from "../../../utils";
import * as actions from "../Calculator/actions";
import EditVesselOnFleetAddStep from "../Dashboard/Vessel/steps/editVesselOnFleetAddStep";
import MyVessel from "./myVessel";

const VESSEL_TOUR_STEPS = [
  {
    target: '[data-spotlight="add-vessel-tab"]',
    message: "Click 'Add Vessel' to start adding your own fleet.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'vessel_tab_open',
  },
  {
    target: '[data-spotlight="add-vessel-actions"]',
    message: "You can manually add a vessel row, or upload a PDF to automatically extract vessel details. Tip: You can download a PDF from an existing vessel and upload it here.",
    placement: 'auto',
    actionRequired: false,
    actionKey: 'add_vessel_actions_seen',
  },
  {
    target: '[data-spotlight="vessel-autocomplete"]',
    message: "Search and select a vessel from the list. We'll auto-populate the technical details.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'vessel_selected',
  },
  {
    target: '[data-spotlight="port-autocomplete"]',
    message: "Search and select the opening port for this vessel.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'port_selected',
  },
  {
    target: '[data-spotlight="verify-vessel-btn"]',
    message: "Click 'Verify' to review and complete the vessel's technical specifications.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'verification_modal_open',
  },
  {
    target: '[data-spotlight="verification-confirm-btn"]',
    message: "Review the vessel's details and click 'Update' to verify them.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'verification_complete',
    nextLabel: 'Finish',
  },
  {
    target: '[data-spotlight="save-fleet-btn"]',
    message: "Finally, click 'Add to My Vessels' to save this vessel to your list.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'vessel_added',
  },
  {
    target: '[data-spotlight="matrix-toggle"]',
    message: "Add Vessel to Matrix: this switch puts your vessel in the matrix. Turn it on, or click Next if it is already on.",
    placement: 'auto',
    actionRequired: true,
    actionKey: 'matrix_toggle',
  },
];
const MATRIX_STEP = VESSEL_TOUR_STEPS.findIndex((step) => step.actionKey === 'matrix_toggle');

const useStyles = makeStyles({
  root: {
    width: "fit-content",
  },
});

const initialSearchType = {
  type: "",
  saveLabel: "",
  name: "",
  firstLoadPort: "",
  lastDischargePort: "",
  loadQuantity: "",
  createdDate: "",
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className="pt-2">
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function Fleet(props) {
  const { loader, setLoader = () => { } } = props;
  const theme = useTheme();

  const thStyle = {
    padding: "5px 6px",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.02em",
    color: "#334155",
    textTransform: "capitalize",
    textAlign: "left",
    borderBottom: "1px solid #cbd5e1",
    whiteSpace: "nowrap",
    background: "#e2e8f0",
  };

  const tdStyle = {
    padding: "0.5px 6px",
    fontSize: 11,
    color: "#1e293b",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };

  const iconBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: "#334155",
    cursor: "pointer",
    transition: "background 0.15s, color 0.15s",
  };
  const [allSavedResults, setAllSavedResults] = useState([]);
  const [allFilters, setAllFilters] = useState({
    offset: 1,
    limit: 50,
    sortBy: "createdDate",
    sortDirection: "desc",
  });
  const [onDateFilter, setonDateFilter] = useState([])
  const [value, setValue] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || window.location.search);
    const tabParam = searchParams.get("tab");
    if (tabParam === "addVessel" || tabParam === "6" || tabParam === "add-vessel") {
      setValue(0);
    }
  }, [location.search]);

  const organizationId = getCurrentUserOrganizationId();
  const [singleResults, setSingleResults] = useState({});
  const [open, setOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [tourState, setTourState] = useState(() => {
    const saved = readGuideProgress("vessel_tab");
    const state = { currentStepIndex: 0, isTooltipVisible: isGuideDone("matrix"), isPaused: !isGuideDone("matrix"), ...saved, suspended: false, isTourCompleted: isGuideDone("vessel") };
    if (state.currentStepIndex > MATRIX_STEP || readGuideProgress("vessel").created) state.currentStepIndex = MATRIX_STEP;
    const selectStep = VESSEL_TOUR_STEPS.findIndex((step) => step.actionKey === 'vessel_selected');
    if (state.currentStepIndex > selectStep && state.currentStepIndex < MATRIX_STEP) state.currentStepIndex = selectStep;
    return state;
  });

  const [isTargetReady, setIsTargetReady] = useState(false);
  const [isWaitingForAction, setIsWaitingForAction] = useState(false);
  const PAUSE_ON_TARGET_CLICK_KEYS = ['vessel_selected', 'port_selected', 'verification_modal_open', 'vessel_added'];
  const CLICKABLE_REQUIRED_KEYS = ['vessel_tab_open', 'verification_modal_open', 'verification_complete', 'vessel_added'];
  const SELECT_KEYS = ['vessel_selected', 'port_selected'];

  const handleActionComplete = useCallback((actionKey) => {
    if (actionKey === 'vessel_add_failed') {
      setTourState(prev => (prev.dismissed ? prev : { ...prev, isPaused: false, isTooltipVisible: true }));
      setIsWaitingForAction(false);
      return;
    }

    if (actionKey === "no_vessels") {
      setTourState(prev => (prev.matrixRequest
        ? { ...prev, matrixRequest: false, currentStepIndex: 0, requested: true, dismissed: false, suspended: false, isPaused: false, isTooltipVisible: true, isTourCompleted: false }
        : prev));
      return;
    }

    if (actionKey === "vessel_added" || actionKey === "vessel_existing") {
      const replaying = isGuideDone("vessel");
      if (replaying && actionKey === "vessel_existing") {
        setTourState(prev => (prev.matrixRequest
          ? { ...prev, matrixRequest: false, currentStepIndex: MATRIX_STEP, requested: true, dismissed: false, suspended: false, isPaused: false, isTooltipVisible: true, isTourCompleted: false }
          : prev));
        return;
      }
      saveGuideProgress("vessel", { created: true });
      setTourState(prev => (replaying && prev.isTourCompleted
        ? prev
        : { ...prev, matrixRequest: false, currentStepIndex: MATRIX_STEP, dismissed: false, suspended: false, isPaused: false, isTooltipVisible: true, isTourCompleted: false }));
      setIsWaitingForAction(false);
      return;
    }
    if (actionKey === "matrix_toggle") {
      if (!isGuideDone("vessel")) {
        if (isGuideDone("matrix")) saveGuideProgress("vessel", { finalPromptPending: true });
        markGuideDone("vessel");
      }
      setTourState(prev => ({ ...prev, currentStepIndex: MATRIX_STEP, isTourCompleted: true, isTooltipVisible: false, isPaused: false }));
      setIsWaitingForAction(false);
      return;
    }
    setTourState(prev => {
      if (prev.isTourCompleted || (!isGuideDone("matrix") && !prev.requested)) return prev;

      const matchedStepIndex = VESSEL_TOUR_STEPS.findIndex((step, index) => (
        index >= prev.currentStepIndex && index < MATRIX_STEP && step.actionKey === actionKey
      ));
      if (matchedStepIndex === -1) return prev;
      return {
        ...prev,
        currentStepIndex: matchedStepIndex + 1,
        dismissed: false,
        isTooltipVisible: true,
        isPaused: false,
        suspended: false,
      };
    });
    setIsWaitingForAction(false);
  }, []);

  useEffect(() => {
    window.handleVesselTourAction = handleActionComplete;
    return () => {
      delete window.handleVesselTourAction;
    };
  }, [handleActionComplete]);

  // Guide Me menu / "Next step" card ask for the vessel guide by key
  useEffect(() => {
    const run = () => {
      if (!takeGuideRequest("vessel")) return;
      if (readGuideProgress("vessel").matrixRequest) {
        saveGuideProgress("vessel", { matrixRequest: false });
        setTourState(prev => ({ ...prev, matrixRequest: true, requested: true, dismissed: false, suspended: false, isPaused: true, isTooltipVisible: false, isTourCompleted: false }));
      } else {
        const onAddTab = new URLSearchParams(window.location.search).get("tab") === "addVessel";
        const replay = isGuideDone("vessel");
        const firstStep = onAddTab ? 1 : 0;
        setTourState(prev => ({ ...prev, currentStepIndex: replay ? firstStep : readGuideProgress("vessel").created ? MATRIX_STEP : onAddTab && prev.currentStepIndex === 0 ? 1 : prev.currentStepIndex, requested: true, dismissed: false, suspended: false, isTooltipVisible: true, isPaused: false, isTourCompleted: false }));
      }
    };
    run();
    window.addEventListener("guide:request", run);
    return () => window.removeEventListener("guide:request", run);
  }, []);

  // When the vessel guide finishes during this visit, offer the way on to the matrix
  const [showMatrixCard, setShowMatrixCard] = useState(() => !!readGuideProgress("vessel").finalPromptPending);
  const vesselTourRan = useRef(!tourState.isTourCompleted);
  useEffect(() => {
    if (!tourState.isTourCompleted) vesselTourRan.current = true;
    else if (vesselTourRan.current) {
      vesselTourRan.current = false;
      setShowMatrixCard(isGuideDone("matrix"));
    }
  }, [tourState.isTourCompleted]);

  useEffect(() => {
    saveGuideProgress("vessel_tab", tourState);
  }, [tourState]);

  // Adjust initial state for tour based on whether info modal will show
  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("auth") || "{}");
    const visitedRoutes = authData.visitedRoutes || [];
    const route = window.location.pathname + window.location.search;
    const willShowPageInfo = (!!pageInfoData[route] && !visitedRoutes.includes(route)) || window.isPageInfoModalScheduled || window.isPageInfoModalOpen;

    if (willShowPageInfo && !tourState.isTourCompleted && !tourState.isPaused) {
      setTourState(prev => ({
        ...prev,
        isTooltipVisible: false,
        isPaused: true
      }));
    }
  }, []);

  // Event listener to resume tour when info modal is closed
  useEffect(() => {
    const handleToggle = (e) => {
      const isModalOpen = e.detail;
      if (!isModalOpen && !tourState.isTourCompleted) {
        setTourState(prev => (prev.dismissed || (!isGuideDone("matrix") && !prev.requested && prev.currentStepIndex !== MATRIX_STEP)
          ? prev
          : { ...prev, isTooltipVisible: true, isPaused: false }));
      }
    };

    window.addEventListener("pageInfoModalToggle", handleToggle);
    return () => {
      window.removeEventListener("pageInfoModalToggle", handleToggle);
    };
  }, [tourState.isTourCompleted]);

  useEffect(() => {
    setTourState(prev => (prev.suspended ? { ...prev, suspended: false } : prev));
  }, [location.search]);

  // Missing targets pause the guide without completing any steps.
  useEffect(() => {
    if (tourState.isTooltipVisible && !tourState.isTourCompleted && !tourState.isPaused && !tourState.suspended && !isWaitingForAction && VESSEL_TOUR_STEPS[tourState.currentStepIndex]) {
      setIsTargetReady(false);
      const currentStep = VESSEL_TOUR_STEPS[tourState.currentStepIndex];
      const targetSelector = currentStep.target;
      let checkAttempts = 0;

      const checkInterval = setInterval(() => {
        const element = document.querySelector(targetSelector);
        if (element) {
          // Target exists, clear the check and show tooltip
          setIsTargetReady(true);
          clearInterval(checkInterval);
        } else {
          checkAttempts++;
          // Allow asynchronous controls to appear before pausing.
          if (checkAttempts >= 300) {
            clearInterval(checkInterval);
            setTourState(prev => ({ ...prev, suspended: true }));
          }
        }
      }, 100);

      // Do an immediate check to prevent flicker delay if element already exists
      if (document.querySelector(targetSelector)) {
        setIsTargetReady(true);
        clearInterval(checkInterval);
      }

      return () => clearInterval(checkInterval);
    } else {
      setIsTargetReady(false);
    }
  }, [tourState.currentStepIndex, tourState.isTooltipVisible, tourState.isTourCompleted, tourState.isPaused, tourState.suspended, isWaitingForAction, location.search]);

  const handleTourNext = () => {
    const currentStep = VESSEL_TOUR_STEPS[tourState.currentStepIndex];
    if (currentStep.actionKey === "matrix_toggle") {
      const target = document.querySelector(currentStep.target);
      const input = target?.querySelector("input");
      if (input?.checked) handleActionComplete("matrix_toggle");
      else input?.click();
      return;
    }
    if (SELECT_KEYS.includes(currentStep.actionKey)) {
      const target = document.querySelector(currentStep.target);
      if (target?.querySelector("input")?.value) {
        setTourState(prev => ({ ...prev, currentStepIndex: prev.currentStepIndex + 1, isTooltipVisible: true, isPaused: false }));
      } else {
        handlePauseTour();
        target?.querySelector("input")?.focus();
        target?.querySelector(".MuiAutocomplete-popupIndicator")?.click();
      }
      return;
    }
    if (currentStep.actionRequired && !CLICKABLE_REQUIRED_KEYS.includes(currentStep.actionKey)) return;

    if (currentStep.actionRequired) {
      document.querySelector(currentStep.target)?.click();
      return;
    }

    setTourState(prev => ({ ...prev, currentStepIndex: prev.currentStepIndex + 1, isTooltipVisible: true, isPaused: false }));
    setIsWaitingForAction(false);
  };

  const handleTourSkip = () => {
    setTourState(prev => ({ ...prev, isPaused: true, dismissed: true, isTooltipVisible: false }));
    setIsWaitingForAction(false);
  };

  const handlePauseTour = () => {
    setTourState(prev => ({ ...prev, isPaused: true, isTooltipVisible: false }));
  };

  const handleResumeTour = () => {
    setTourState(prev => ({ ...prev, isPaused: false, isTooltipVisible: true }));
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue == 1) {
      getAllOrgSavedResults()
    }
  };

  const [searchValue, setSearchValue] = useState("");
  const onSearchVessel = (search) => {
    getAllOrgSavedResults({ search: search });
  };
  const delaySearch = useCallback(
    _.debounce((data) => onSearchVessel(data), 500),
    []
  );
  const onSearchChange = (e) => {
    setSearchValue(e.target.value)
    delaySearch(e.target.value);
  }

  useEffect(() => {
    getAllOrgSavedResults()
  }, [])

  const getDwtValue = (item) => {
    const parsed = (() => {
      try {
        return typeof item?.vessel === "string" ? JSON.parse(item?.vessel || "{}") : (item?.vessel || {});
      } catch (e) {
        return {};
      }
    })();
    return Number(item?.dwt || parsed?.vesselDwt || parsed?.dwt || parsed?.DWT || 0);
  };

  const getVesselNameValue = (item) => {
    const parsed = (() => {
      try {
        return typeof item?.vessel === "string" ? JSON.parse(item?.vessel || "{}") : (item?.vessel || {});
      } catch (e) {
        return {};
      }
    })();
    return (parsed?.vesselName || item?.vesselName || "").toLowerCase();
  };

  const getAllOrgSavedResults = async (fromPagination) => {
    if (!hasFetched && (!allSavedResults || allSavedResults.length === 0)) {
      setIsInitialLoading(true);
      setLoader(true);
    }
    await actions
      .userFleets({ ...fromPagination, access: "public", organizationId: organizationId, isPending: false })
      .then((res) => {
        setIsInitialLoading(false);
        setLoader(false);
        setHasFetched(true);
        let list = res?.data?.list || [];
        const activeSortBy = fromPagination?.sortBy || allFilters.sortBy;
        const activeSortDirection = fromPagination?.sortDirection || allFilters.sortDirection;

        if (activeSortBy === "dwt" && list.length) {
          const dir = activeSortDirection === "asc" ? 1 : -1;
          list = [...list].sort((a, b) => (getDwtValue(a) - getDwtValue(b)) * dir);
        } else if (activeSortBy === "vesselName" && list.length) {
          const dir = activeSortDirection === "asc" ? 1 : -1;
          list = [...list].sort((a, b) => getVesselNameValue(a).localeCompare(getVesselNameValue(b)) * dir);
        }

        setAllSavedResults(list);
        setAllFilters((prev) => ({
          ...prev,
          ...fromPagination,
          totalPages: res?.data?.totalItems,
          offset: res?.data?.currentPage,
          pageCount: res?.data?.totalPages,
          limit: res?.data?.perPage
        }))
      })
      .catch((err) => {
        setIsInitialLoading(false);
        setLoader(false);
        setHasFetched(true);
        toastError(err?.data?.message);
      });
  };

  const onChangeMyPagination = (type = "") => {
    let newAllFilters = {};
    if (type === "inc") {
      newAllFilters = { ...allFilters, offset: allFilters.offset + 1 };
    } else if (type === "first") {
      newAllFilters = { ...allFilters, offset: 1 };
    } else {
      if (allFilters.offset === 1) return;
      newAllFilters = { ...allFilters, offset: allFilters.offset - 1 };
    }

    getAllOrgSavedResults({ ...newAllFilters, search: searchValue })
  };

  const handleSortClick = (sortKey) => {
    let newSortDirection = "asc";
    if (allFilters.sortBy === sortKey) {
      if (allFilters.sortDirection === "asc") newSortDirection = "desc";
      else if (allFilters.sortDirection === "desc") {
        newSortDirection = sortKey === "createdDate" ? "asc" : "";
      }
    } else {
      newSortDirection = sortKey === "createdDate" ? "desc" : "asc";
    }

    let apiFilters = { ...allFilters, sortBy: sortKey, sortDirection: newSortDirection, offset: 1 };
    if (!newSortDirection) {
      apiFilters.sortBy = "createdDate";
      apiFilters.sortDirection = "desc";
    }

    getAllOrgSavedResults({ ...apiFilters, search: searchValue });
  };

  const onClickEditVessel = async (item) => {
    setSingleResults({ ...item, vesseldata: JSON.parse(item?.vessel) } || {});
    // setIsEdit(true)
    setOpen(true)

  }

  const removeExtraFieldFromAuto = ({ _id, nearestport, vesseldata, ...rest }) => ({
    ...rest,
  });
  const onUpdateMyVessel = () => {

    setLoader(true);

    const resulted = singleResults?.map((item) => {
      return {
        ...removeExtraFieldFromAuto(item),
        ...item?.nearestport,
        dateOpen: moment(item?.nearestDate?.date).toISOString(),
        userId: getCurrentUserId(),
        vesselId: item?.vesselId,
        access: "private",
        vessel: JSON.stringify(
          {
            ...(item.vesseldata),
            createdUser: { _id: item.vesseldata?.createdUser }
          }),
      };
    });
    setOpen(false)
    setLoader(true);
    actions
      .addMultiFleet(resulted)
      .then((res) => {
        setLoader(false);
        toastSuccess("Vessel(s) Copy added  successfully");
      })
      .catch((err) => {
        // setAddFleetModal(false);
        setLoader(false);
        toastError(err?.data?.message || "Something went wrong");
      });
    setOpen(false)
  }
  const handleDateRangeChange = async (newDateRange) => {
    const { startDate, endDate } = newDateRange;
    // Ensure dates are in ISO string format
    const formattedStartDate = startDate?.toISOString() || '';
    const formattedEndDate = endDate?.toISOString() || '';

    if (value == 1) {
      const { data } = await actions.userFleets({
        access: 'public',
        organizationId: organizationId,
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      setAllSavedResults(data.list);
    } else {
      const { data } = await actions.userFleets({
        userId: getCurrentUserId(),
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      setonDateFilter(data.list);
    }
  };

  const tabs = [
    { key: 0, label: "My Vessel", tooltip: "Displays vessels entries created by you." },
    { key: 1, label: "All Vessel", tooltip: "Displays all vessels shared with your organization" },
  ];

  return (
    <Grid container alignContent="flex-start" className="min-h-[calc(100vh-106px)]">

      <Grid item xs={12}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={2}
        >
          <Box>
            <CustomTabs tabs={tabs} active={value} setActive={setValue} />
          </Box>

          <Box display="flex" alignItems="center" gap={2} className="ml-auto">
            {(value === 1 || (new URLSearchParams(location.search || window.location.search).get("tab") !== "addVessel" && new URLSearchParams(location.search || window.location.search).get("tab") !== "6" && new URLSearchParams(location.search || window.location.search).get("tab") !== "add-vessel")) && (
              <div className="desktop-searchbar">
                <SearchBar
                  onDateRangeChange={handleDateRangeChange}
                  placeholder={"Search with Vessel Name or Port..."}
                  setSearchValue={onSearchChange}
                  searchValue={searchValue}
                />
              </div>
            )}
            <GuideMenu />
          </Box>
        </Box>

        {/* Mobile search bar */}
        {(value === 1 || (new URLSearchParams(location.search || window.location.search).get("tab") !== "addVessel" && new URLSearchParams(location.search || window.location.search).get("tab") !== "6" && new URLSearchParams(location.search || window.location.search).get("tab") !== "add-vessel")) && (
          <Box
            className="md:hidden"
            display="flex"
            justifyContent="center"
            mt={2}
          >
            <div className="w-full">
            <SearchBar
              isMobile
              onDateRangeChange={handleDateRangeChange}
              placeholder={"Search with Vessel Name or Port..."}
              setSearchValue={onSearchChange}
              searchValue={searchValue}
            />
            </div>
          </Box>
        )}
      </Grid>
      <div className="add-fleet-modal-container w-full">
        <TabPanel value={value} index={0} className="px-0">
          <MyVessel {...props} loader={loader} setLoader={setLoader} DatefilterValue={onDateFilter} setSearchValue={onSearchChange} searchValue={searchValue} delaySearch={delaySearch} />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          <div
            style={{
              background: "#fff",
              borderRadius: 0,
              border: "1px solid #cbd5e1",
              overflow: "hidden",
              minHeight: (loader || !allSavedResults?.length) ? "500px" : "auto",
            }}
            className="shadow-sm"
          >
            {allFilters?.totalPages > allFilters?.limit && (
              <div
                style={{
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "1px",
                }}
              >
                <CustomPagination
                  onChangePagination={onChangeMyPagination}
                  filters={allFilters}
                  totalPage={allFilters?.totalPages}
                />
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table className="fit-cols-mobile" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                <thead>
                  <tr style={{ background: "#e2e8f0" }}>
                    <th style={{ ...thStyle, width: 36, textAlign: "center" }}>No.</th>
                    <th style={{ ...thStyle, width: "18%", cursor: "pointer" }} onClick={() => handleSortClick("vesselName")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Vessel Name
                        <div style={{ color: allFilters.sortBy === "vesselName" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "vesselName" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "vesselName" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "8%", cursor: "pointer" }} onClick={() => handleSortClick("dwt")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        DWT
                        <div style={{ color: allFilters.sortBy === "dwt" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "dwt" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "dwt" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "16%", cursor: "pointer" }} onClick={() => handleSortClick("port")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Port
                        <div style={{ color: allFilters.sortBy === "port" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "port" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "port" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "11%", cursor: "pointer" }} onClick={() => handleSortClick("dateOpen")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Date Opening
                        <div style={{ color: allFilters.sortBy === "dateOpen" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "dateOpen" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "dateOpen" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "10%", cursor: "pointer" }} onClick={() => handleSortClick("hirePerDay")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Hire Per Day
                        <div style={{ color: allFilters.sortBy === "hirePerDay" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "hirePerDay" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "hirePerDay" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "8%", cursor: "pointer" }} onClick={() => handleSortClick("hireGbb")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Hire GBB
                        <div style={{ color: allFilters.sortBy === "hireGbb" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "hireGbb" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "hireGbb" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "12%" }}>Remark</th>
                    <th style={{ ...thStyle, width: "10%", cursor: "pointer" }} onClick={() => handleSortClick("createdDate")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        Created on
                        <div style={{ color: allFilters.sortBy === "createdDate" ? "#0f172a" : "#64748b", display: "flex", alignItems: "center" }}>
                          {allFilters.sortBy === "createdDate" && allFilters.sortDirection === "desc" ? <LuArrowDown size={13} /> : allFilters.sortBy === "createdDate" && allFilters.sortDirection === "asc" ? <LuArrowUp size={13} /> : <LuArrowDownUp size={13} />}
                        </div>
                      </div>
                    </th>
                    <th style={{ ...thStyle, width: "10%" }}>Created By</th>
                    <th style={{ ...thStyle, width: 60, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isInitialLoading && (!Array.isArray(allSavedResults) || !allSavedResults.length) ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: "center" }}>
                        <Box display="flex" justifyContent="center" alignItems="center" height="440px">
                          {allSavedResults?.length > 0 && <CircularProgress />}
                        </Box>
                      </td>
                    </tr>
                  ) : Array.isArray(allSavedResults) && allSavedResults.length ? (
                    allSavedResults.map((item, index) => {
                      const parsedVessel = (() => {
                        try {
                          return typeof item?.vessel === "string"
                            ? JSON.parse(item?.vessel || "{}")
                            : (item?.vessel || {});
                        } catch (e) {
                          return {};
                        }
                      })();
                      const vesselName = parsedVessel?.vesselName || item?.vesselName || "—";
                      const vesselDwt = item?.dwt || parsedVessel?.vesselDwt || parsedVessel?.dwt || parsedVessel?.DWT || 0;

                      return (
                        <tr
                          key={item._id || index}
                          onDoubleClick={() => { onClickEditVessel(item); }}
                          style={{
                            transition: "background 0.15s",
                            cursor: "pointer",
                            backgroundColor: index % 2 === 1 ? "#f8fafc" : "#ffffff",
                          }}
                          className="hover:bg-slate-100/90 transition-colors"
                        >
                          <td style={{ ...tdStyle, color: "#64748b", textAlign: "center" }}>
                            {(allFilters.offset - 1) * allFilters.limit + index + 1}
                          </td>
                          <td style={{ ...tdStyle, color: "#1e293b" }}>
                            {vesselName}
                          </td>
                          <td style={tdStyle}>
                            {vesselDwt ? Number(vesselDwt).toLocaleString() : 0}
                          </td>
                          <td style={tdStyle}>
                            {item.port || "—"}
                          </td>
                          <td style={tdStyle}>
                            {moment(item.dateOpen).format("DD/MMM/yyyy")}
                          </td>
                          <td style={tdStyle}>
                            {item.hirePerDay || "—"}
                          </td>
                          <td style={tdStyle}>
                            {item.hireGbb || 0}
                          </td>
                          <td style={tdStyle}>
                            <CustomTooltip text={item.remark || ""}>
                              <span className="block truncate max-w-[250px]">
                                {item.remark || "—"}
                              </span>
                            </CustomTooltip>
                          </td>
                          <td style={tdStyle}>
                            {item.createdDate ? moment(item.createdDate).format("DD/MMM/yyyy") : "—"}
                          </td>
                          <td style={tdStyle}>
                            {item?.createdUser
                              ? `${item.createdUser?.firstName} ${item.createdUser?.lastName}`
                              : "—"}
                          </td>
                          <td style={{ ...tdStyle, textAlign: "center" }}>
                            <CustomTooltip position="top" text="Copy Vessel details to my vessel">
                              <button
                                onClick={() => onClickEditVessel(item)}
                                style={iconBtnStyle}
                                title="Edit"
                                onMouseEnter={(e) => { e.currentTarget.style.color = "#1d4ed8"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = "#334155"; }}
                              >
                                <FiEdit2 size={17} />
                              </button>
                            </CustomTooltip>
                          </td>
                        </tr>
                      );
                    })
                  ) : hasFetched ? (
                    <tr>
                      <td colSpan={11} style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8", fontSize: 14 }}>
                        No Vessel Found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {allFilters?.totalPages > allFilters?.limit && (
              <div
                style={{
                  borderTop: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "1px",
                }}
              >
                <CustomPagination
                  onChangePagination={onChangeMyPagination}
                  filters={allFilters}
                  totalPage={allFilters?.totalPages}
                />
              </div>
            )}
          </div>
        </TabPanel>

      </div>
      <CustomShowModal
        open={open}
        disableClose
        handleClose={() => setOpen(false)}
        maxWidth="md"
        title="Copy Vessel Details"
      >

        <EditVesselOnFleetAddStep type="copy" setValidationVesselFromAuto={onUpdateMyVessel} setValidationVessel={setOpen} setAddFleetsArray={setSingleResults} addFleetsArray={[singleResults]} onValidationVesselIndex={0} isFromCreateVessel={true} />
      </CustomShowModal>

      {!tourState.isTourCompleted && isTargetReady && !tourState.isPaused && !tourState.suspended && VESSEL_TOUR_STEPS[tourState.currentStepIndex] && (
        <SpotlightOverlay
          targetSelector={VESSEL_TOUR_STEPS[tourState.currentStepIndex].target}
          active={tourState.isTooltipVisible}
          message={VESSEL_TOUR_STEPS[tourState.currentStepIndex].message}
          onDismiss={handleTourSkip}
          onNext={handleTourNext}
          onSkip={handleTourSkip}
          position={VESSEL_TOUR_STEPS[tourState.currentStepIndex].placement}
          nextLabel={VESSEL_TOUR_STEPS[tourState.currentStepIndex].nextLabel || "Next"}
          targetPadding={2}
          targetRadius={6}
          onTargetClick={() => {
            const currentStep = VESSEL_TOUR_STEPS[tourState.currentStepIndex];
            if (currentStep && PAUSE_ON_TARGET_CLICK_KEYS.includes(currentStep.actionKey)) {
              handlePauseTour();
            }
          }}
        />
      )}
      {showMatrixCard && (
        <GuideContinueCard
          title="Vessel step complete"
          text="Your cargo and vessel are in the matrix. Now move to the Matrix to compare them."
          label="Continue"
          guideKey="view"
          onClose={() => {
            saveGuideProgress("vessel", { finalPromptPending: false });
            setShowMatrixCard(false);
          }}
        />
      )}
    </Grid>
  );
}

export default Fleet;
